import { debounce, deepCompare } from '@agilepoint/utils-common';
import React, { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Editor } from 'roosterjs-editor-core';
import { getFormatState } from 'roosterjs-editor-api';
import { FormatState, IEditor } from 'roosterjs-editor-types';
import { IChangeDetector } from '../plugins';

export const ToolBarActionContext = createContext<{ editor: IEditor }>({ editor: new Editor(document.createElement('div')) });

export function useToolbarActionContext() {
    return useContext(ToolBarActionContext);
}

const FormatStateContext = createContext<FormatState>({});

interface IFormatStateProviderProps {
    children?: ReactNode | ReactNode[];
    editor: IEditor;
    changeDetector: IChangeDetector;
}

export function FormatStateProvider(props: IFormatStateProviderProps) {
    const { editor, children, changeDetector } = props;
    const [state, setState] = useState<FormatState>(getFormatState(editor));
    const prevStateRef = useRef(state);

    const updateFormatState = useMemo(() => debounce(() => {
        const newState = getFormatState(editor);
        if (deepCompare(newState, prevStateRef.current)) {
            return;
        }
        setState(newState);
        prevStateRef.current = newState;
    }, 10), [editor]);

    useEffect(() => {
        changeDetector.subscribe(updateFormatState);
        return () => {
            changeDetector.unsubscribe(updateFormatState);
        };
    }, [changeDetector, editor, updateFormatState]);

    return (
        <FormatStateContext.Provider value={state}>
            {children}
        </FormatStateContext.Provider>
    );
}

export function useFormatState() {
    return useContext(FormatStateContext);
}
